<template>
  <div class="events">
    <header class="events__header">
      <div
        v-if="isSearchVisible"
        class="events__search"
      >
        <component
          v-for="item in searchFields"
          :key="`search_field_${item.id}`"
          :ref="`field_${item.id}`"
          :is="item.component"
          :value="search[item.id]"
          class="events__field"
          :class="{ events__field_active: searchStep === item.id }"
          :has-valid-status="false"
          has-clear-button
          @focus="setSearchStep(item.id)"
          @input="changeSearchField(item.id, $event)"
        />
        <BaseIconButton
          icon="cross"
          class="events__close"
          @click="hideSearch"
        />
      </div>
      <div v-else class="events__filters">
        <div class="events__tabs">
          <button
            v-for="(tab, index) in tabs"
            :key="`tab_${index}`"
            type="button"
            class="events__tab"
            :class="{ events__tab_active: tab.isActive }"
            v-text="tab.label"
            @click="toggleMonthFilter"
          />
        </div>
        <BaseIconButton
          icon="loupe"
          @click="showSearch"
          class="events__toggle"
        />
      </div>
      <BaseNote
        v-if="note"
        v-bind="note"
        class="events__note"
      />
    </header>
    <div ref="content" class="events__content pretty-scrollbar">
      <BaseTitle
        v-if="notfoundText"
        :text="notfoundText"
        size="m"
        class="events__notfound"
      />
      <slot
        name="content"
        :items="visibleItems"
      />
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

import parseDateFromString from '@/helpers/parseDateFromString';
import BaseTitle from '@/components/BaseTitle.vue';
import BaseIconButton from '@/components/BaseIconButton.vue';
import BaseNote from '@/components/BaseNote.vue';
import FieldSearch from '@/components/FieldSearch.vue';
import FieldDate from '@/components/FieldDate.vue';
import FieldTime from '@/components/FieldTime.vue';

import CONSTANTS from '@/helpers/constants';

const {
  USER_REPLIC,
  ACTION_CLEAR_SEARCH_NAME,
  ACTION_CLEAR_SEARCH_DATE,
  ACTION_CLEAR_SEARCH_TIME,
  ACTION_CLEAR_SEARCH,
} = CONSTANTS;

const SEARCH_FIELDS = Object.freeze({
  NAME: 'name',
  DATE: 'date',
  TIME: 'time',
});

const SEARCH_WORD_TRIGGER = 'найти';

export default {
  name: 'Events',
  components: {
    BaseTitle,
    BaseIconButton,
    BaseNote,
    FieldSearch,
    FieldDate,
    FieldTime,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    query: {
      type: Object,
      default: () => ({}),
    },
    isSortAscending: {
      type: Boolean,
      default: true,
    },
    actionNote: {
      type: String,
      default() {
        return this.$t('texts.buyActionNote');
      },
    },
  },
  data() {
    return {
      isSearchVisible: false,
      isMonthFilterActive: true,
      searchStep: SEARCH_FIELDS.NAME,
      search: {},
      isSearchPending: false,
    };
  },
  watch: {
    query: 'parseQuery',
  },
  computed: {
    locale() {
      return this.$i18n.locale;
    },
    nearestItem() {
      const [firstItem] = this.itemsSortedByDate;
      return firstItem;
    },
    nearestMonthLabel() {
      const date = this.nearestItem?.date || Date.now();
      return new Intl.DateTimeFormat(this.locale, { month: 'long' }).format(date);
    },
    searchName() {
      return this.search[SEARCH_FIELDS.NAME];
    },
    searchDate() {
      return this.search[SEARCH_FIELDS.DATE];
    },
    searchTime() {
      return this.search[SEARCH_FIELDS.TIME];
    },
    hasSearchQuery() {
      return Boolean(this.searchName || this.searchDate || this.searchTime);
    },
    itemsSortedByDate() {
      const sortFunc = this.isSortAscending
        ? (one, two) => Date.parse(one.date) - Date.parse(two.date)
        : (one, two) => Date.parse(two.date) - Date.parse(one.date);
      return [...this.items].sort(sortFunc);
    },
    itemsFilterByMonth() {
      const nearestMonth = (this.nearestItem?.date || new Date()).getMonth();
      const nearestYear = (this.nearestItem?.date || new Date()).getFullYear();
      return this.itemsSortedByDate
        .filter((item) => item.date.getMonth() === nearestMonth && item.date.getFullYear() === nearestYear);
    },
    itemsFilterBySearch() {
      if (!this.hasSearchQuery) return [];

      const parsedDate = parseDateFromString(this.searchDate);

      return this.itemsSortedByDate
        .filter((item) => {
          let isMatch = true;

          // Проверка по имени
          if (isMatch && this.searchName) {
            const query = this.prepareToCompare(this.searchName);
            const value = this.prepareToCompare(item.performance?.title || '');
            isMatch = value.includes(query);
          }

          // Проверка по дате
          if (isMatch && this.searchDate) {
            const query = parsedDate?.toLocaleDateString();
            const value = item.date.toLocaleDateString();
            isMatch = value === query;
          }

          // Проверка по времени
          if (isMatch && this.searchTime) {
            const query = this.searchTime.slice(0, 5);
            const value = [
              String(item.date.getHours()).padStart(2, '0'),
              String(item.date.getMinutes()).padStart(2, '0'),
            ].join(':');
            isMatch = value === query;
          }

          return isMatch;
        });
    },
    visibleItems() {
      // Поиск
      if (this.isSearchVisible) {
        return this.itemsFilterBySearch;
      }
      // Фильтр по месяцу
      if (this.isMonthFilterActive) {
        return this.itemsFilterByMonth;
      }
      return this.itemsSortedByDate;
    },
    isSingleItemVisible() {
      return this.visibleItems.length === 1;
    },
    searchFields() {
      return [
        {
          id: SEARCH_FIELDS.NAME,
          component: FieldSearch,
        },
        {
          id: SEARCH_FIELDS.DATE,
          component: FieldDate,
        },
        {
          id: SEARCH_FIELDS.TIME,
          component: FieldTime,
        },
      ];
    },
    tabs() {
      return [
        {
          label: this.nearestMonthLabel,
          isActive: this.isMonthFilterActive,
        },
        {
          label: this.$t('actions.all'),
          isActive: !this.isMonthFilterActive,
        },
      ];
    },
    notfoundText() {
      if (!this.isSearchVisible || !this.hasSearchQuery || this.visibleItems.length) {
        return '';
      }
      return this.$t('texts.notfound');
    },
    noteText() {
      if (this.isSingleItemVisible) return this.actionNote;
      const stepNotes = {
        [SEARCH_FIELDS.NAME]: this.$t('texts.searchNameNote'),
        [SEARCH_FIELDS.DATE]: this.$t('texts.searchDateNote'),
        [SEARCH_FIELDS.TIME]: this.$t('texts.searchTimeNote'),
      };
      const step = this.isSearchVisible ? this.searchStep : SEARCH_FIELDS.NAME;
      return stepNotes[step];
    },
    note() {
      return {
        icon: { name: 'voice' },
        text: { text: this.noteText },
      };
    },
  },
  created() {
    this.initActions();
    this.parseQuery();
  },
  methods: {
    ...mapActions([
      'parseSearchRequest',
    ]),
    changeSearchField(id, value) {
      this.$set(this.search, id, value);
    },
    toggleMonthFilter() {
      this.isMonthFilterActive = !this.isMonthFilterActive;
      this.clearContentScroll();
    },
    showSearch() {
      this.isSearchVisible = true;
      this.$nextTick(this.setSearchFocus);
      this.clearContentScroll();
    },
    hideSearch() {
      this.isSearchVisible = false;
      this.clearContentScroll();
      this.clearSearchFields();
      return document.activeElement?.blur();
    },
    setSearchFocus() {
      const [firstField] = this.$refs[`field_${SEARCH_FIELDS.NAME}`] || [];
      return firstField?.focus();
    },
    clearContentScroll() {
      if (this.$refs.content) {
        this.$refs.content.scrollTop = 0;
      }
    },
    clearSearchFields() {
      this.search = {};
    },
    setSearchStep(step) {
      this.searchStep = step;
    },
    parseQuery() {
      this.search = {
        ...this.search,
        ...this.query,
      };
      this.isSearchVisible = this.hasSearchQuery;
      this.setActiveStepBySearch();
    },
    setActiveStepBySearch() {
      if (this.searchName && this.searchDate) {
        this.setSearchStep(SEARCH_FIELDS.TIME);
        return;
      }
      this.setSearchStep(this.searchName ? SEARCH_FIELDS.DATE : SEARCH_FIELDS.NAME);
    },
    clearSearchName() {
      this.changeSearchField(SEARCH_FIELDS.NAME, '');
    },
    clearSearchDate() {
      this.changeSearchField(SEARCH_FIELDS.DATE, '');
    },
    clearSearchTime() {
      this.changeSearchField(SEARCH_FIELDS.TIME, '');
    },
    onUserReplic(replic) {
      const hasTriggerWord = replic.toLowerCase().startsWith(SEARCH_WORD_TRIGGER);
      if (hasTriggerWord && !this.isSearchPending) {
        const regex = new RegExp(`^${SEARCH_WORD_TRIGGER}`, 'i');
        const request = replic.replace(regex, '').trim();
        const isOnlyTime = /^\d{1,2}:\d{2}$/.test(request);
        this.isSearchPending = true;
        this.parseSearchRequest({
          string: request,
          need_increase: this.isSortAscending,
        })
          .then((result) => this.setSearchByUserReplic(result, isOnlyTime))
          .catch(console.error)
          .finally(() => {
            this.isSearchPending = false;
          });
      }
    },
    setSearchByUserReplic({ text, date, time }, isOnlyTime) {
      const isTimeStep = isOnlyTime || this.searchStep === SEARCH_FIELDS.TIME;
      if (text && !isTimeStep) {
        this.changeSearchField(SEARCH_FIELDS.NAME, text);
      }
      if (date && !isTimeStep) {
        const options = { day: 'numeric', month: 'long', year: 'numeric' };
        const prettyDate = new Intl.DateTimeFormat(this.$i18n.locale, options)
          .format(new Date(date));
        this.changeSearchField(SEARCH_FIELDS.DATE, prettyDate);
      }
      if (time) {
        this.changeSearchField(SEARCH_FIELDS.TIME, time);
      }
      this.isSearchVisible = this.hasSearchQuery;
      this.setActiveStepBySearch();
      this.clearContentScroll();
    },
    prepareToCompare(str) {
      return str.toLowerCase()
        .replace(/ё/g, 'е')
        .replace(/[,.\s-]/g, '');
    },
    initActions() {
      const rootActions = [
        [USER_REPLIC, this.onUserReplic],
        [ACTION_CLEAR_SEARCH_NAME, this.clearSearchName],
        [ACTION_CLEAR_SEARCH_DATE, this.clearSearchDate],
        [ACTION_CLEAR_SEARCH_TIME, this.clearSearchTime],
        [ACTION_CLEAR_SEARCH, this.hideSearch],
      ];

      rootActions.forEach((action) => this.$root.$on(...action));

      this.$once('hook:beforeDestroy', () => {
        rootActions.forEach((action) => this.$root.$off(...action));
      });
    },
  },
};
</script>

<style lang="stylus">
.events
  position relative
  display flex
  flex-direction column

  // &:after
  //   content ''
  //   abs bottom left right
  //   height 3em
  //   background linear-gradient(180deg, alpha(#fff, 0), #fff)
  //   pointer-events none
  //   z-index 1

  &__header
    padding-left $side_padding
    padding-right $side_padding
    padding-bottom 1em
    margin-top -1em

  &__search
    display flex
    align-items center

  &__close
    width 3em
    height @width
    padding 0.6em

  &__toggle
    abs top 50% left 100%
    transform translate(1.5em, -50%)

  &__note
    margin-top 0.5em

  &__filters
    position relative
    max-width 18em
    margin 0 auto

  &__tabs
    display flex
    height 3em

  &__tab
    flex-grow 1
    flex-basis 0
    max-width 50%
    height 100%
    background alpha($c_bg, 50%)
    border 0
    margin 0
    padding 0
    font-family inherit
    font-size 1em
    font-weight 600
    color $c_dim
    text-transform uppercase
    &_active
      pointer-events none
      background-color $c_text
      color #fff

  &__content
    flex 1
    min-height 0
    overflow-y scroll
    padding-left $side_padding
    padding-right 'calc(%s - %s)' % ($side_padding ($scroll_width * 1px))
    padding-bottom 5.2em

  &__notfound
    text-align center
    padding 1.5em

  &__field
    display flex
    flex-grow 1
    flex-basis 0
    max-width 10em
    padding-right 1em

    .field__label
      display flex
      align-items center
      justify-content center
      width 100%
      font-size 1em
      text-transform uppercase
      font-weight 700
      letter-spacing .03em
      padding-left 1.5em
      padding-right @padding-left
      color inherit
      border solid 0.125em $c_text
      margin 0
      &:after
        display none

    .field__container
      width 0
      overflow hidden
      opacity 0

    &.field_has_value
      .field__label
        background-color $c_text
        color #fff

    &.field_disabled
      .field__label
        opacity 0.5

    &_active
      max-width 100%
      .field__label
        display none
      .field__container
        width 100%
        opacity 1
</style>
